import { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Card, FormControl, LoadingButton, PageTitle, PageWrapper } from '@ingrid/luterkort-react';

import { FormLeaveBlocker } from '~src/components/FormLeaveBlocker/FormLeaveBlocker';
import { MutationBanner } from '~src/components/MutationNotification/MutationNotification';
import { useTranslation } from '~src/i18n/use-translation';
import { useToast } from '~src/main/hooks/useToast';
import { hasTouchedFields } from '~src/utils/form';

import { Breadcrumb } from '../breadcrumb';
import { Menu } from '../menu';
import { useCarrierContractCreateMutation } from '../mutations';
import { CarrierContractUpsert, useCarrierContractForm } from '../types';
import { AuthFormModal } from './auth-form-modal';
import { BasicInfo } from './basic-info';
import { Carrier } from './carrier';
import { Credentials } from './credentials';

interface Props {
  defaultValues?: CarrierContractUpsert;
}

export function Page({ defaultValues }: Props) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthFormOpen, setIsAuthFormOpen] = useState(false);

  const form = useCarrierContractForm({
    defaultValues: defaultValues ?? {
      description: '',
      name: '',
    },
  });

  const disableClear = !form.formState.isDirty || !hasTouchedFields(form.formState.touchedFields);
  const mutation = useCarrierContractCreateMutation();
  const schemaId = form.watch('schema_id');
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { toast } = useToast();

  async function onSubmit(data: CarrierContractUpsert) {
    await mutation.mutateAsync(data);

    toast({
      title: t('carrierContracts.details.addSuccess'),
      variant: 'success',
    });

    navigate('..');
  }

  function handleAuthComplete(data: object) {
    setIsAuthenticated(true);

    form.setValue('carrier_configuration_data', {
      ...form.getValues('carrier_configuration_data'),
      ...data,
    });
  }

  function handleClear() {
    form.resetField('name');
    form.resetField('description');
    form.resetField('schema_id');
    form.resetField('carrier_configuration_data', { defaultValue: {} });
  }

  return (
    <PageWrapper variant="center">
      <FormProvider {...form}>
        <FormControl.Root>
          <FormControl.Form onSubmit={form.handleSubmit(onSubmit)} style={{ display: 'none' }} />

          <PageTitle.Wrapper>
            <PageTitle.Stack>
              <Breadcrumb currentPage={t('common.addNew')} />
            </PageTitle.Stack>

            <FormControl.SubmitButton asChild>
              <LoadingButton loading={mutation.isPending}>{t('common.create')}</LoadingButton>
            </FormControl.SubmitButton>

            <Menu disabled={disableClear}>
              <Menu.ClearItem onClick={handleClear} disabled={disableClear} />
            </Menu>
          </PageTitle.Wrapper>

          <MutationBanner mutation={mutation} />

          <Card.List>
            <BasicInfo />
            <Carrier />
            <Credentials onShowAuthForm={() => setIsAuthFormOpen(true)} isAuthFormDisabled={isAuthenticated} />
          </Card.List>

          <FormLeaveBlocker formState={form.formState} />
        </FormControl.Root>
      </FormProvider>

      {schemaId && (
        <AuthFormModal
          onAuthComplete={handleAuthComplete}
          setIsOpen={setIsAuthFormOpen}
          isOpen={isAuthFormOpen}
          schemaId={schemaId}
        />
      )}
    </PageWrapper>
  );
}
